import React, { Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { clearErrors } from '../Redux/ActionCreators/errorAction';
import { useDispatch } from 'react-redux';
import Footer from './footer';
import { useTranslation } from 'react-i18next';

// Captured once at module load — reflects system DPI scaling without any browser zoom.
// Comparing current DPR against this baseline isolates browser zoom changes only.
const NATURAL_DPR = window.devicePixelRatio;

const isMobileOrTablet = () => {
    const ua = navigator.userAgent;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    // pointer:coarse = touch-primary device (phones, tablets, iPad Pro in desktop mode)
    // MacBook trackpads report pointer:fine, so this safely excludes all Macs
    const isTouchPrimary = window.matchMedia('(pointer: coarse)').matches;
    return isMobileUA || isTouchPrimary;
};

export default function CandidateHeader() {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { candidateDetails, testDetails } = useSelector(state => state.candidateTest);
    const error = useSelector(state => state.error);

    const isNotebook = sessionStorage.getItem("isNotebook") === "true";

    // The candidate-test screen decides for itself whether a phone is allowed
    // (it knows the assessment's proctoring setting). Every other candidate
    // route keeps the desktop-only gate.
    const isCandidateTestRoute = window.location.pathname.startsWith("/candidate-test/");
    const isNonDesktop = isMobileOrTablet() && !isCandidateTestRoute;

    const [zoomWarning, setZoomWarning] = useState(false);

    useEffect(() => {
        const checkZoom = () => {
            // Compare current DPR against the baseline captured at load time.
            // This gives the browser zoom factor independent of screen DPI / Retina scaling.
            const zoomFactor = window.devicePixelRatio / NATURAL_DPR;
            setZoomWarning(zoomFactor > 1.25);
        };
        checkZoom();
        window.addEventListener('resize', checkZoom);
        return () => window.removeEventListener('resize', checkZoom);
    }, []);

    const snackClosed = () => {
        dispatch(clearErrors());
    }

    return (
        <div>
            {isNonDesktop ? (
                <div className="candidate-holder-sm">
                    <p>
                        This assessment can only be taken on a laptop or desktop with a minimum screen size of 11.5 inches.
                    </p>
                </div>
            ) : (
                <div className='candidate-holder candidate-holder--responsive' style={{ overflow: 'auto', maxHeight: '100vh', background:'#EDF2F7', minHeight: '100vh' }}>
                    {zoomWarning && (
                        <div style={{
                            position: 'sticky', top: 0, zIndex: 999,
                            background: '#FEF3C7', borderBottom: '1px solid #F59E0B',
                            padding: '10px 20px', display: 'flex',
                            justifyContent: 'center', alignItems: 'center', gap: '12px',
                        }}>
                            <span style={{ fontSize: '13px', color: '#92400E' }}>
                                Your browser zoom level is currently above 125%. For the best assessment experience, please reset it to 100%.
                            </span>
                            <button
                                onClick={() => setZoomWarning(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#92400E', lineHeight: 1 }}
                                aria-label="Dismiss zoom warning"
                            >
                                ✕
                            </button>
                        </div>
                    )}
                    <div className='candidate-header d-flex flex-column align-items-center' style={{ zIndex: 1, borderBottom: "1px solid #e8eaed", paddingBottom:'20px' }}>
                        {!isNotebook &&
                            <>
                                {candidateDetails?.logoImage  ? <img src={candidateDetails?.logoImage} alt='' width='auto' style={{height:'50px'}} /> : <img src='/images/logo.png' width='auto' style={{height:'50px'}} alt='' />}
                            </>
                        }
                    </div>
                    <div className='layout-container' style={{minHeight:'70vh'}}>
                        <Snackbar
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center', }}
                            open={error.successMessage}
                            autoHideDuration={3000}
                            onClose={snackClosed}
                        >
                            <Alert onClose={snackClosed} severity="error" sx={{ width: '100%' }}>
                                {error.successMessageText}
                            </Alert>
                        </Snackbar>
                        <Snackbar
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center', }}
                            open={error.successEnable}
                            onClose={() => snackClosed()}
                            autoHideDuration={4000}
                        >
                            <Alert onClose={snackClosed} severity="success" sx={{ width: '100%' }}>
                                {error.successMessageText}
                            </Alert>
                        </Snackbar>
                        <React.Suspense fallback={''}>
                            <Outlet />
                        </React.Suspense>
                    </div>
                    <div className='text-center mt-5 mb-3'>
                        <Footer />
                    </div>
                </div>
            )}
        </div>
    )
}
